<template>
  <div>
    <video
      :id="`videoStream${playerId}`"
      ref="videoPlayer"
      class="video-js"
      :controls="controls"
    ></video>
  </div>
</template>

<script>
import mpegts from 'mpegts.js'
import { mapGetters } from 'vuex'

export default {
  name: 'FlvPlayer',
  props: {
    src: { type: String, required: true },
    playerId: { type: String, default: '' },
    controls: { type: Boolean, default: false },
    isRecording: { type: Boolean, default: false },
    type: { type: String, required: true }, // 'stream' or 'playback'
  },
  data() {
    return {
      player: null,
      flv: null,
      supportedType: '',
      mediaRecorder: null,
      recordedChunks: [],
      attemptsStaticticInfo: 0,

      // this state to handle pending state
      isPending: false,
      isPlaying: false,
      maxPendingTimeout: 20000,
      pendingTimeout: null,

      // Simpan reference ke handler biar bisa di-remove
      eventHandlers: {},
      flvHandlers: {},
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
  },
  async mounted() {
    if (this.src) {
      if (this.type === 'playback') {
        this.$store.commit('vehicle/setTimePlaybackActive', null)
      }
      this.initialize()
    }
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    initialize() {
      if (!mpegts.isSupported()) return
      if (this.flv) this.cleanup()

      this.player = this.$refs.videoPlayer
      const isLive = this.type === 'stream'

      this.flv = mpegts.createPlayer(
        {
          type: 'flv',
          url: this.src,
          isLive,
        },
        {
          isLive,
          lazyLoad: isLive,
          autoCleanupSourceBuffer: isLive,
          enableWorker: true,
        }
      )

      this.flv.attachMediaElement(this.player)
      this.flv.load()
      this.flv.play()

      this.attemptsStaticticInfo = 0

      // Simpan handler supaya bisa dilepas nanti
      this.flvHandlers.statistics = (data) => {
        if (this.type === 'stream' && this.getFeatureFlagValue('trackvision_is_srs_cluster')) {
          if (data.speed === 0) {
            this.handlePlayerFreeze()
          } else {
            clearTimeout(this.pendingTimeout)
            this.pendingTimeout = null
            this.isPlaying = true
            this.isPending = false
          }
        }
        this.attemptsStaticticInfo++
        this.$nextTick().then(() => {
          const kbps = data.speed
          this.$emit('loadeddata', data, kbps.toFixed(2))
        })
      }

      this.flvHandlers.error = (err) => {
        this.$emit('error')
        this.attemptsStaticticInfo = 0
      }

      this.flv.on(mpegts.Events.STATISTICS_INFO, this.flvHandlers.statistics)
      this.flv.on(mpegts.Events.ERROR, this.flvHandlers.error)

      // Event listeners on video
      this.eventHandlers.playing = () => this.$emit('ready')
      this.eventHandlers.timeupdate = (e) => this.$emit('timeupdate', e)

      // Detect when MediaSource ends or stream freezes
      this.eventHandlers.ended = (e) => {
        console.log('Video ended/frozen - MediaSource ended')
        this.handlePlayerFreeze()
      }

      this.player.addEventListener('playing', this.eventHandlers.playing)
      this.player.addEventListener('timeupdate', this.eventHandlers.timeupdate)
      this.player.addEventListener('ended', this.eventHandlers.ended)
    },

    handlePlayerFreeze() {
      // handle pending or player freeze
      this.isPending = true
      this.isPlaying = false
      if (!this.pendingTimeout) {
        clearTimeout(this.pendingTimeout)
        this.pendingTimeout = setTimeout(() => {
          this.$emit('pendingTimeout')
          this.cleanup()
          this.attemptsStaticticInfo = 0
        }, this.maxPendingTimeout)
      }
    },

    cleanup() {
      if (this.player) {
        this.player.removeEventListener('playing', this.eventHandlers.playing)
        this.player.removeEventListener('timeupdate', this.eventHandlers.timeupdate)
        this.player.removeEventListener('ended', this.eventHandlers.ended)
      }

      if (this.flv) {
        if (this.flvHandlers.statistics) {
          this.flv.off(mpegts.Events.STATISTICS_INFO, this.flvHandlers.statistics)
        }
        if (this.flvHandlers.error) {
          this.flv.off(mpegts.Events.ERROR, this.flvHandlers.error)
        }

        if (this.type === 'playback') {
          this.flv.destroy()
        } else if (this.type === 'stream') {
          this.$emit('destroyed', this.playerId, this.flv)
        }

        this.flv = null
      }

      this.player = null
    },
  },
}
</script>
